import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../api/users';
import { authApi } from '../../api/auth';
import { queryKeys } from '../queryClient';
import type { Role } from '../../types/domain';

/** Admin user management. The gateway blocks non-admins, so these 403 for others. */
export function useUsers(role?: Role) {
  return useQuery({ queryKey: queryKeys.users(role), queryFn: () => usersApi.list(role) });
}

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => authApi.listRoles(), staleTime: 60 * 60_000 });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; roles?: Role[]; active?: boolean }) =>
      usersApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

/**
 * Admin-initiated account creation. IdentityService has no dedicated admin "create user"
 * endpoint — AuthController.Register is the only account-creation path, and it already
 * accepts an arbitrary `role` (not restricted to "customer"), so it's reused here rather
 * than adding a parallel endpoint. `extraRoles`/`active: false` are applied with a follow-up
 * UsersController.Update call since Register only sets a single role and always creates the
 * account active.
 */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; name: string; password: string; role: Role; extraRoles?: Role[]; active?: boolean }) => {
      const { userId } = await authApi.register({ email: input.email, name: input.name, password: input.password, role: input.role });
      // One built-in role only (see IdentityService UsersController.Update) — extras are custom roles.
      const builtIn = ['admin', 'manager', 'worker', 'customer'];
      const roles = Array.from(new Set([input.role, ...(input.extraRoles ?? []).filter((r) => !builtIn.includes(r))]));
      if (roles.length > 1 || input.active === false) {
        await usersApi.update(userId, { roles: roles.length > 1 ? roles : undefined, active: input.active });
      }
      return userId;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
